import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Sparkles } from 'lucide-react';
import {
  MAX_TAGS_PER_TASK,
  normalizeTagName,
  sortTagsForPicker,
  suggestTagsFromText,
  validateTagName,
} from '../../utils/taskTags';
import './TaskTagPicker.css';
import './TaskTagChips.css';

/**
 * Multi-select tag picker for task create/edit flows.
 */
export default function TaskTagPicker({
  tags = [],
  selectedIds = [],
  onChange,
  allowCreate = false,
  onCreateTag,
  suggestFromText = '',
  recentIds = [],
  usageCounts = {},
  disabled = false,
  compact = false,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const tagsById = useMemo(() => new Map(tags.map((tg) => [tg.id, tg])), [tags]);

  const sortedTags = useMemo(
    () => sortTagsForPicker(tags.filter((tg) => tg.isActive !== false), { recentIds, usageCounts }),
    [tags, recentIds, usageCounts],
  );

  const suggestions = useMemo(() => {
    if (!suggestFromText?.trim()) return [];
    const suggested = suggestTagsFromText(suggestFromText, tags);
    return suggested.filter((tg) => !selectedIds.includes(tg.id));
  }, [suggestFromText, tags, selectedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTags;
    return sortedTags.filter((tg) => tg.name.toLowerCase().includes(q));
  }, [sortedTags, query]);

  const selectedTags = selectedIds
    .map((id) => tagsById.get(id))
    .filter(Boolean);

  const atLimit = selectedIds.length >= MAX_TAGS_PER_TASK;

  const toggle = (id) => {
    if (disabled) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else if (!atLimit) {
      onChange([...selectedIds, id]);
    }
  };

  const applySuggestions = () => {
    if (disabled || atLimit) return;
    const toAdd = suggestions.map((tg) => tg.id).filter((id) => !selectedIds.includes(id));
    const room = MAX_TAGS_PER_TASK - selectedIds.length;
    onChange([...selectedIds, ...toAdd.slice(0, room)]);
  };

  const handleCreate = async () => {
    const name = normalizeTagName(query);
    const err = validateTagName(name, tags.map((tg) => tg.name));
    if (err || !onCreateTag) return;
    setCreating(true);
    try {
      const newId = await onCreateTag(name);
      if (newId && !atLimit) onChange([...selectedIds, newId]);
      setQuery('');
    } finally {
      setCreating(false);
    }
  };

  const canCreate = allowCreate
    && query.trim()
    && !validateTagName(query, tags.map((tg) => tg.name))
    && !filtered.some((tg) => tg.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className={`task-tag-picker${compact ? ' task-tag-picker--compact' : ''}${disabled ? ' is-disabled' : ''}`}>
      {selectedTags.length > 0 && (
        <div className="task-tag-picker-selected">
          {selectedTags.map((tg) => (
            <button
              key={tg.id}
              type="button"
              className={`task-tag-picker-chip task-tag-chip task-tag-chip--${tg.color || 'slate'}`}
              onClick={() => toggle(tg.id)}
              disabled={disabled}
              title={t('tags.removeTag')}
            >
              {tg.name}
              <X size={12} aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {suggestions.length > 0 && !disabled && (
        <div className="task-tag-picker-suggestions">
          <button type="button" className="task-tag-suggest-btn" onClick={applySuggestions}>
            <Sparkles size={13} aria-hidden="true" />
            {t('tags.applySuggestions', { count: suggestions.length })}
          </button>
          <div className="task-tag-picker-suggest-chips">
            {suggestions.map((tg) => (
              <button
                key={tg.id}
                type="button"
                className={`task-tag-picker-option task-tag-chip task-tag-chip--${tg.color || 'slate'}`}
                onClick={() => toggle(tg.id)}
                disabled={atLimit}
              >
                {tg.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {!disabled && (
        <div className="task-tag-picker-input-row">
          <input
            type="text"
            className="form-control task-tag-picker-search"
            placeholder={atLimit ? t('tags.maxReached') : t('tags.searchOrAdd')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                handleCreate();
              }
            }}
            disabled={atLimit && !query}
            maxLength={40}
          />
          {canCreate && (
            <button
              type="button"
              className="btn btn-sm btn-secondary task-tag-picker-create"
              onClick={handleCreate}
              disabled={creating}
            >
              <Plus size={14} />
              {t('tags.create')}
            </button>
          )}
        </div>
      )}

      {!atLimit && !disabled && filtered.length > 0 && (
        <div className="task-tag-picker-options" role="listbox" aria-label={t('tags.availableTags')}>
          {filtered.map((tg) => {
            const isSelected = selectedIds.includes(tg.id);
            return (
              <button
                key={tg.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`task-tag-picker-option task-tag-chip task-tag-chip--${tg.color || 'slate'}${isSelected ? ' is-selected' : ''}`}
                onClick={() => toggle(tg.id)}
              >
                {tg.name}
              </button>
            );
          })}
        </div>
      )}

      {atLimit && (
        <p className="task-tag-picker-hint text-sm text-muted">{t('tags.maxReached')}</p>
      )}
    </div>
  );
}
